import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Lock, Pencil, Check, X, ArrowRight } from "lucide-react";

/**
 * Editable-with-audit inline field. Benchmark/management values are locked but
 * editable by full-access roles; every change requires a reason and is audited.
 *
 * Props:
 *  - value: current numeric value
 *  - display: (v) => string  (formatter)
 *  - canEdit: boolean
 *  - onSave: async (newValue:number, reason:string) => void   (must throw on error)
 *  - label: string (dialog context)
 *  - edited: boolean, lastChanged: string
 *  - testPrefix: string
 */
export function GovernedEditableField({
  value, display = (v) => String(v), canEdit = false, onSave, label = "value",
  edited = false, lastChanged = "", suffix = "", testPrefix = "governed-field", align = "right",
  valueClassName = "tabular-nums font-medium", showLock = true,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ""));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const start = () => { setDraft(String(value ?? "")); setEditing(true); };
  const cancel = () => { setEditing(false); setReason(""); };
  const newVal = parseFloat(draft);
  const changed = !isNaN(newVal) && newVal !== value;

  const openConfirm = () => {
    if (!changed) { cancel(); return; }
    setDialogOpen(true);
  };
  const confirm = async () => {
    if (!reason.trim()) { toast.error("A reason is required for governed edits."); return; }
    setSaving(true);
    try {
      await onSave(newVal, reason.trim());
      toast.success(`Updated ${label}`);
      setDialogOpen(false); setEditing(false); setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save change");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <span className={cn("inline-flex items-center gap-1", align === "right" && "justify-end")}>
        <Input
          type="number" value={draft} autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") openConfirm(); if (e.key === "Escape") cancel(); }}
          className="h-7 w-[92px] text-right text-sm tabular-nums"
          data-testid={`${testPrefix}-input`}
        />
        <Button size="icon" variant="ghost" className="h-7 w-7 p-0 text-[hsl(var(--success))]" onClick={openConfirm} disabled={!changed} data-testid={`${testPrefix}-confirm-button`}>
          <Check className="h-4 w-4" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground" onClick={cancel} data-testid={`${testPrefix}-cancel-button`}>
          <X className="h-4 w-4" />
        </Button>

        <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <AlertDialogContent data-testid={`${testPrefix}-audit-dialog`}>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm governed change</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="ml-auto flex items-center gap-2 font-medium tabular-nums text-foreground">
                      {display(value)} <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" /> <span className="text-primary">{display(newVal)}{suffix}</span>
                    </span>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-foreground">Reason for change (required)</label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                      placeholder="e.g. Board approved revised Q1 target" data-testid={`${testPrefix}-audit-reason`} />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={confirm} disabled={saving || !reason.trim()} data-testid={`${testPrefix}-audit-confirm`}>
                {saving ? "Saving…" : "Confirm & log"}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </span>
    );
  }

  return (
    <span className={cn("group inline-flex items-center gap-1.5", align === "right" && "justify-end")}>
      <span className={valueClassName}>{display(value)}{suffix}</span>
      {edited ? (
        <Badge variant="outline" className="border-[hsl(var(--warning)/0.35)] bg-[hsl(var(--warning)/0.10)] px-1.5 py-0 text-[9px] text-[hsl(var(--warning))]" data-testid={`${testPrefix}-edited-badge`}>edited</Badge>
      ) : (
        showLock && <Lock className="h-3 w-3 text-muted-foreground" data-testid={`${testPrefix}-lock-icon`} />
      )}
      {canEdit && (
        <Button size="icon" variant="ghost" className="h-7 w-7 p-0 opacity-60 transition-opacity duration-150 hover:opacity-100" onClick={start} data-testid={`${testPrefix}-edit-button`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      {lastChanged && <span className="hidden text-[10px] text-muted-foreground xl:inline" data-testid={`${testPrefix}-last-changed`}>{lastChanged}</span>}
    </span>
  );
}
